// "editar antes de usar": el especialista puede corregir a mano el texto de una
// sugerencia de IA (Meta, Objetivo, o el Comentario de III-A/III-B) ANTES de aplicarla, en vez
// de solo poder "Refinar" (pedirle un ajuste a la IA) o usarla tal cual. Este mixin es el
// pedacito de estado/lógica COMÚN a los 3 lugares donde eso ya pasa (GoalsManager.vue/
// SubAreaNecessity.vue -> tarjeta de Meta, SubAreaGoalCallout.vue -> tarjeta de Objetivo,
// Tab3.vue -> bloque de sugerencia de III-A/III-B) -- mismo criterio que ya se sigue en este
// proyecto para lógica compartida entre esos mismos archivos (ver aiBulkSelectMixin.js).
//
// No asume la forma exacta del objeto "sugerencia": cada consumidor le pasa una función
// `getDisplayText(suggestion)` (qué texto mostrar/editar -- para Metas es la vista compuesta
// formatAiGoalPreview, para Objetivos/III-A/III-B es el campo de texto libre real) y guarda el
// resultado editado en `suggestion._aiEditText`, agregado con Vue.set para que sea reactivo en
// arrays de sugerencias (Vue 2 no detecta la asignación de una propiedad nueva sin esto).
//
// Auditoría (LogAiGenerationAppliedField, ver PeiEngine.SaveStudentFunction/
// AddAcademicAreaGoalAsync/AddPeiProgramServiceObjective): el consumidor es quien decide, al
// guardar, si manda el aiGenerationLogId real o null -- este mixin solo expone
// `aiEditWasEdited()` para que esa decisión sea un simple `if`, no vuelve a implementar la
// comparación en cada archivo.
export default {
  methods: {
    // Arranca el modo edición: guarda el texto que se estaba mostrando como punto de partida.
    // getDisplayText puede ser un string (nombre de propiedad) o una función(suggestion) -> string.
    aiEditStart(suggestion, getDisplayText) {
      const initial = this._aiEditResolveText(suggestion, getDisplayText);
      this.$set(suggestion, '_aiEditText', initial);
      this.$set(suggestion, '_aiEditing', true);
    },
    // Descarta la edición en curso -- vuelve a la sugerencia original de la IA, sin tocarla.
    aiEditCancel(suggestion) {
      this.$set(suggestion, '_aiEditing', false);
      this.$set(suggestion, '_aiEditText', null);
    },
    aiEditIsActive(suggestion) {
      return !!(suggestion && suggestion._aiEditing);
    },
    // botón único (ícono lápiz/X, ver .ai-btn-icon) que reemplaza al par de botones
    // "Editar"/"Cancelar" de texto -- mockup aprobado por el usuario para descargar la fila de
    // acciones de abajo a un solo botón. Mismo criterio de aiEditStart/aiEditCancel, solo que
    // decide cuál de los dos llamar según el estado actual, para no repetir el `if` en cada
    // consumidor.
    aiEditToggle(suggestion, getDisplayText) {
      if (this.aiEditIsActive(suggestion)) {
        this.aiEditCancel(suggestion);
      } else {
        this.aiEditStart(suggestion, getDisplayText);
      }
    },
    // Texto a usar al GUARDAR: el editado si hubo edición, si no getSaveText (que para Metas es
    // distinto de getDisplayText -- ver el comentario en GoalsManager.vue/applyAiGoalSuggestion).
    aiEditFinalText(suggestion, getSaveText) {
      if (this.aiEditHasEditedText(suggestion)) {
        return suggestion._aiEditText;
      }
      return this._aiEditResolveText(suggestion, getSaveText);
    },
    // true si el especialista de verdad escribió/confirmó un texto editado (distinto de haber
    // solo entrado en modo edición y cancelado, o de nunca haber editado nada).
    aiEditHasEditedText(suggestion) {
      return !!(suggestion && suggestion._aiEditText !== null && suggestion._aiEditText !== undefined);
    },
    // Señal SOLO visual (la etiqueta "Editado") -- compara contra getDisplayText, que es lo que
    // el especialista tenía delante cuando empezó a editar. La señal de auditoría real (qué
    // aiGenerationLogId mandar al guardar) usa aiEditHasEditedText(), no esta función.
    aiEditWasEdited(suggestion, getDisplayText) {
      if (!this.aiEditHasEditedText(suggestion)) return false;
      const original = this._aiEditResolveText(suggestion, getDisplayText);
      return (suggestion._aiEditText || '').trim() !== (original || '').trim();
    },
    _aiEditResolveText(suggestion, getText) {
      if (typeof getText === 'function') return getText(suggestion) || '';
      return (suggestion && suggestion[getText]) || '';
    },
    // El modelo a veces devuelve el texto libre de una sugerencia (Actividad de la Meta,
    // descripción del Objetivo, Comentario de III-A/III-B) partido en párrafos (saltos de
    // línea) -- esos campos son de texto corrido, no de párrafos, así que se colapsan los
    // saltos de línea/espacios repetidos a un solo espacio. Se llama UNA VEZ, al parsear la
    // respuesta de la IA (antes de guardar el campo en la sugerencia), para que quede
    // consistente en todos lados: la vista previa, "editar antes de usar" (aiEditStart) y el
    // uso directo sin editar (que no pasa por este mixin -- ver applyAiObjectiveSuggestion/
    // applyAiGoalSuggestion/handleUseVersion).
    aiFlattenSuggestionText(text) {
      return (text || '').replace(/\s*\r?\n+\s*/g, ' ').replace(/[ \t]{2,}/g, ' ').trim();
    },
  },
};
